import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useCreateWorkspace } from '@/queries/workspaces/useCreateWorkspace'
import { useUpdateWorkspace } from '@/queries/workspaces/useUpdateWorkspace'
import { useAppDispatch, useAppSelector } from '@/redux/hooks/useAppState'
import {
	isCreateWorkspaceDialogOpen,
	setIsCreateWorkspaceDialogOpen,
	setIsWorkspaceSelectorOpen,
} from '@/redux/store/slices/ui/uiSlice'
import { BaseWorkspaceValidator, validateForm } from 'honeygrid-types'
import { type FC } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import type { BaseWorkspace, Workspace } from 'honeygrid-types'

interface CreateWorkspaceDialogProps {
	workspace?: Workspace | null
	afterClose?: () => void
}

export const CreateWorkspaceDialog: FC<CreateWorkspaceDialogProps> = ({
	afterClose,
	workspace,
}) => {
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const isOpen = useAppSelector(isCreateWorkspaceDialogOpen)
	const { mutateAsync: createWorkspace } = useCreateWorkspace()
	const { mutateAsync: updateWorkspace } = useUpdateWorkspace()

	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
		reset,
	} = useForm({
		defaultValues: {
			name: workspace?.name ?? '',
		},
	})

	const closeDialog = () => {
		dispatch(setIsCreateWorkspaceDialogOpen(false))
		reset()
		afterClose?.()
	}

	const onSubmit = async (values: { name: string }) => {
		// Validate using the existing validator
		const validationResult = validateForm(BaseWorkspaceValidator, values)
		if (validationResult) {
			return
		}

		if (!workspace) {
			const draftWorkspace: BaseWorkspace = {
				name: values.name,
			}

			const newWorkspace = await createWorkspace(draftWorkspace)
			navigate(`/${newWorkspace.organization_slug}/locations/add`)
			closeDialog()
		} else {
			await updateWorkspace({
				uuid: workspace.uuid,
				name: values.name,
			})

			closeDialog()
			dispatch(setIsWorkspaceSelectorOpen(true))
		}
	}

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{workspace ? 'Update' : 'Create new'} workspace</DialogTitle>
				</DialogHeader>

				<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
					<div className="space-y-2">
						<Input
							{...register('name', {
								validate: (value) => {
									const validationResult = validateForm(BaseWorkspaceValidator, { name: value })
									return validationResult?.name || undefined
								},
							})}
							placeholder="Workspace Name"
							className={errors.name ? 'border-destructive' : ''}
						/>
						{errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={closeDialog} type="button">
							Cancel
						</Button>
						<Button disabled={!isValid} type="submit">
							Save
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}
